import produce from 'immer';
import { registerReducer } from '../../../StateSetup/RootReducer';
import Day from '@awesomecomponents/day-js';
import { ExtendStudentStatus } from './ExtendStudentStatus';

import { isChildWithinStudentAgeRange } from '../utils';
import { CommitContext } from '../../App/ReducerActions/CommitContext';

const { BFF_URL, GBCORE_CLIENT_NAME, GBCORE_CLIENT_VERSION } = window.__ENV__;

// Action names
const ACTION_NAME_PREFIX = 'action/addFamilyMember';
export const REQUEST_ACTION_NAME = `${ACTION_NAME_PREFIX}/request`;
export const RESPONSE_ACTION_NAME = `${ACTION_NAME_PREFIX}/response`;
export const ERROR_ACTION_NAME = `${ACTION_NAME_PREFIX}/error`;

/**
 * Performs up to 4 mutations to save family members data in the manuconnect:
 * - createFamilyMember mutation to save family member data,
 * - createOrUpdateFamilyMemberCOBs to save family member's cob data.
 * - extendStudentStatus to extend student status of overage child returning to school
 * - commitContext to commit previous mutations
 * @param {object} familyMember
 * @param {array} cobs
 * @param {object} logger
 * @returns {Promise<object>}
 */
export const AddFamilyMember = (familyMember, cobBenefits) => {
    return async (dispatch, getState, { logger }) => {
        // step 1: Request Action
        dispatch({ type: REQUEST_ACTION_NAME });

        const { jwt, sessionCorrelationId, language } = getState().Authentication;
        const transactionId = logger.generateGuid();
        const dependents = getState().MemberData.familyMembers;
        const dateOfBirth = new Day(familyMember.dateOfBirth.date);
        const { studentMaxAge, childDependentMaxAge: studentMinAge } = getState().MemberData.member.plan;

        // start creating family member
        const familyMemberToAdd = {
            firstName: familyMember.firstName,
            lastName: familyMember.lastName,
            birthDate: familyMember.dateOfBirth.date,
            effectiveDate: familyMember.effectiveDate,
            gender: familyMember.gender,
            familyMemberType: familyMember.familyMemberType,
            status: 'ACTIVE',
        };

        logger.info(`${ACTION_NAME_PREFIX}: Saving family Member.`, {
            correlation: { transactionId },
        });
        // Use Case -> If spouse is already terminated in the manuconnect and member is adding the same spouse again
        // Instead of creating a new record, with updateFamilyMember mutation terminated spouse is reinstated to active.
        const terminatedSpouseToReinstate = dependents.find(
            (dependent) =>
                familyMember.familyMemberType === 'SPOUSE' &&
                familyMember.firstName === dependent.firstName &&
                familyMember.lastName === dependent.lastName &&
                dateOfBirth.toString() === dependent.birthDate
        );

        let familyMemberPersonId;

        if (terminatedSpouseToReinstate) {
            logger.info(
                `${ACTION_NAME_PREFIX}: Updating member's terminated spouse. (personId: ${terminatedSpouseToReinstate.personId})`,
                { correlation: { transactionId } }
            );

            const spouseMutation = await fetch(`${BFF_URL}/api/v2`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'gb-authorization': jwt,
                    'mli-session-correlation-id': sessionCorrelationId,
                    'mli-correlation-id': transactionId,
                    'mli-channel': 'web',
                    'gb-client-name': GBCORE_CLIENT_NAME,
                    'gb-client-version': GBCORE_CLIENT_VERSION,
                    'Accept-Language': language,
                    'no-commit': true,
                },
                body: JSON.stringify({
                    operationName: 'updateTerminatedFamilyMember',
                    variables: {
                        familyMember: {
                            personId: terminatedSpouseToReinstate.personId,
                            status: 'ACTIVE',
                        },
                    },
                    query: `
                    mutation updateTerminatedFamilyMember($familyMember: FamilyMemberUpdateInput){
                        updateFamilyMember(familyMember:$familyMember){
                            personId
                            firstName
                            lastName
                            birthDate
                            gender
                            familyMemberType
                            status
                        }
                    }
                     `,
                }),
            });

            const spouseMutationResponse = await spouseMutation.json();

            if (spouseMutationResponse.errors) {
                const { message, code } = spouseMutationResponse.errors[0];
                logger.error(
                    `${ACTION_NAME_PREFIX}: Error updating terminated spouse. Code: ${code} Reason: ${message}`,
                    { correlation: { transactionId } }
                );
                return dispatch({
                    type: ERROR_ACTION_NAME,
                    code,
                    message,
                });
            }
            familyMemberPersonId = spouseMutationResponse.data.updateFamilyMember.personId;
        } else {
            logger.info(`${ACTION_NAME_PREFIX}: Adding family member.`, { correlation: { transactionId } });

            const familyMemberMutation = await fetch(`${BFF_URL}/api/v2`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'gb-authorization': jwt,
                    'mli-session-correlation-id': sessionCorrelationId,
                    'mli-correlation-id': transactionId,
                    'mli-channel': 'web',
                    'gb-client-name': GBCORE_CLIENT_NAME,
                    'gb-client-version': GBCORE_CLIENT_VERSION,
                    'Accept-Language': language,
                    'no-commit': true,
                },
                body: JSON.stringify({
                    operationName: 'createFamilyMember',
                    variables: {
                        familyMember: familyMemberToAdd,
                    },
                    query: `
                    mutation createFamilyMember($familyMember: FamilyMemberCreateInput){
                        createFamilyMember(familyMember:$familyMember){
                            personId
                            firstName
                            lastName
                            birthDate
                            effectiveDate
                            gender
                            familyMemberType
                            status
                        }
                    }
                     `,
                }),
            });

            const familyMemberMutationResponse = await familyMemberMutation.json();

            if (familyMemberMutationResponse.errors) {
                const { message, code } = familyMemberMutationResponse.errors[0];
                logger.error(`${ACTION_NAME_PREFIX}: Error adding family member. Code: ${code} Reason: ${message}`, {
                    correlation: { transactionId },
                });
                return dispatch({
                    type: ERROR_ACTION_NAME,
                    code,
                    message,
                });
            }
            familyMemberPersonId = familyMemberMutationResponse.data.createFamilyMember.personId;
        }

        const cobBenefitsSelected = cobBenefits
            // It returns an array of benefits which are currently selected by the user while selecting the cobs.
            .filter(
                (cobBenefit) => familyMember.kindOfCoverageSelected[cobBenefit.value] && familyMember.hasOtherCoverage
            )
            // Maps cobs field to build an array of cobs
            .map((coordinationOfBenefit) => ({
                personId: familyMemberPersonId,
                benefitCode: coordinationOfBenefit.value,
                memberBenefitId: coordinationOfBenefit.id,
                cobPolicyNumber: familyMember.cobContractNumber,
                cobClaimCertNumber: familyMember.cobCertificateNumber,
                otherInsurerName: familyMember.cobProvider,
                hasOtherInsurer: familyMember.hasOtherCoverage,
                coveredDependentType: 'INCLUSION',
            }));

        const cobBenefitsNotSelected = cobBenefits
            // It returns an array of benefits which are not currently selected by
            // the user while selecting the cobs OR benefit(s) selected when there is no other coverage.
            .filter(
                (cobBenefit) =>
                    !familyMember.kindOfCoverageSelected[cobBenefit.value] ||
                    (familyMember.kindOfCoverageSelected[cobBenefit.value] && !familyMember.hasOtherCoverage)
            )
            // Maps cobs field to build an array of cobs
            .map((cobBenefitNotSelected) => ({
                personId: familyMemberPersonId,
                benefitCode: cobBenefitNotSelected.value,
                memberBenefitId: cobBenefitNotSelected.id,
                cobPolicyNumber: '',
                cobClaimCertNumber: '',
                hasOtherInsurer: false,
                coveredDependentType: 'INCLUSION',
            }));

        const coordinationOfBenefits = [...cobBenefitsSelected, ...cobBenefitsNotSelected];

        logger.info(`${ACTION_NAME_PREFIX}: Adding family Member COBS. (personId: ${familyMemberPersonId})`, {
            correlation: { transactionId },
        });

        const familyMemberAddCobMutation = await fetch(`${BFF_URL}/api/v2`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'gb-authorization': jwt,
                'mli-session-correlation-id': sessionCorrelationId,
                'mli-correlation-id': transactionId,
                'mli-channel': 'web',
                'gb-client-name': GBCORE_CLIENT_NAME,
                'gb-client-version': GBCORE_CLIENT_VERSION,
                'Accept-Language': language,
                'no-commit': true,
            },
            body: JSON.stringify({
                operationName: 'createFamilyMemberCOBs',
                variables: {
                    COBs: [...coordinationOfBenefits],
                },
                query: `
                    mutation createFamilyMemberCOBs($COBs: [familyMemberCOBInput]){
                        createOrUpdateFamilyMemberCOBs(COBs:$COBs){
                            benefitCode
                            dependentBenefitId
                            memberBenefitId
                            hasOtherInsurer
                            cobPolicyNumber
                            cobClaimCertNumber
                            otherInsurerName
                            coveredDependentType
                        }
                    }
                    `,
            }),
        });
        const familyMemberAddCobMutationResponse = await familyMemberAddCobMutation.json();

        if (familyMemberAddCobMutationResponse.errors) {
            const { message, code } = familyMemberAddCobMutationResponse.errors[0];
            logger.error(
                `${ACTION_NAME_PREFIX}: Error adding family member cobs records. Code: ${code} Reason: ${message}`,
                { correlation: { transactionId } }
            );
            return dispatch({
                type: ERROR_ACTION_NAME,
                code,
                message,
            });
        }

        //extend student status if new child is overage child returning to school and child is within plan student age
        if (
            familyMember.additionReason === 'OverAgeChildReturningToSchool' &&
            isChildWithinStudentAgeRange(familyMemberToAdd, studentMaxAge, studentMinAge)
        ) {
            const extendStudentStatusResponse = await dispatch(
                ExtendStudentStatus({ familyMemberToAdd, personId: familyMemberPersonId }, true)
            );

            if (!extendStudentStatusResponse.success) {
                const { message, code } = extendStudentStatusResponse;
                return dispatch({ type: ERROR_ACTION_NAME, code, message });
            }
        }

        //commit earlier changes if they were all successful
        logger.info(`${ACTION_NAME_PREFIX}: Committing changes.`, { correlation: { transactionId } });

        const commitContextResponse = await dispatch(CommitContext());

        // Once successful response is received from the above mutations,
        // updated family member information is retrieved and response is dispatched and
        // saved in global state(redux).
        if (!commitContextResponse.success) {
            const { message, code } = commitContextResponse;
            return dispatch({
                type: ERROR_ACTION_NAME,
                code,
                message,
            });
        }
        return dispatch({
            type: RESPONSE_ACTION_NAME,
            success: true,
        });
    };
};

/**
 * Dispatched before the gb-core-2 requests are sent to indicate loading and reset error state.
 */
export const requestReducer = produce((state) => {
    state.Errors.familyMemberUpdate.hasError = false;
    state.MemberData.isAddFamilyDataSaving = true;
});

/**
 * When we get a successful response, save the family members in the state
 * Reset the isAddFamilyDataSaving is false so the add button is not grey out
 */
export const responseReducer = produce((state) => {
    state.MemberData.isAddFamilyDataSaving = false;
});

/**
 * Called when either call to gb-core-2 fails so we can display an error to the user.
 * @param {object} state
 * @param {object} action
 */
export const errorReducer = produce((state, action) => {
    state.MemberData.isAddFamilyDataSaving = false;
    state.Errors.familyMemberUpdate.hasError = true;
    state.Errors.familyMemberUpdate.code = action.code;
    state.Errors.familyMemberUpdate.message = action.message;
});

registerReducer(REQUEST_ACTION_NAME, requestReducer);
registerReducer(RESPONSE_ACTION_NAME, responseReducer);
registerReducer(ERROR_ACTION_NAME, errorReducer);
